import React, { useContext, useEffect, useState } from 'react';
import moment from 'moment';
import Typography from '../../Typography';
import Button from '../../Button';
import { AlertContext } from '../../SuccessOrFailedModal';
import { PermissionProvider } from '../../../hoc/withPermissions';
import { SIEMBA_CUSTOMER } from '../../../utils/roles';
import { useValidateLogin } from '../../../hooks/auth';
import { FEATURE_KEYS, isFeatureHidden } from '../../../utils/constants/featureAccess';
import {
  formatMaskedCardNumber,
  hasMaskedDigits,
  resolvePaymentMethodIcon,
  resolvePaymentMethodLabel,
} from '../../../utils/constants/paymentMethodLabels';
import {
  useBillingOverview,
  useCancelSubscription,
  useCreatePortalSession,
  useResumeSubscription,
} from '../../../hooks/billing';

/**
 * ROAR-4944 — the Payments & Invoices pane (AC4, AC5, AC6, AC7, AC10).
 *
 * **Siemba renders no invoice table and no card form** (AC10). Both live inside the Stripe Customer
 * Portal, which both buttons reach through one endpoint with a `flow` discriminator. The Figma
 * frame for this pane still draws an in-app invoice table with per-row Download links; it pre-dates
 * the 2026-09-15 portal-delegation decision and is stale. Only its headings and Stripe copy are used.
 *
 * **Cancellation is a two-state control keyed off `cancellation_requested_at`** (ROAR-4899 §4
 * decision 16) — see `isCancellationPending` below for why `plan.status` must never be consulted.
 *
 * Read-only display stays visible to `customer_engineer`; only the ACTION controls are gated to
 * `customer_leader` (§4 decision 10).
 */

/** Figma shows "Aug 1, 2026". A missing date drops the clause rather than printing "Invalid date". */
const formatDate = (value) => (value ? moment(value).format('MMM D, YYYY') : null);

/**
 * Whether a cancellation has been requested but has not yet taken effect.
 *
 * **Keyed off `cancellation_requested_at` alone, never `plan.status`.** Stripe leaves the
 * subscription `active` for the whole pending window — the status only changes when P1.12 /
 * ROAR-4934's `customer.subscription.deleted` webhook lands at period end. Branching on `status`
 * would render "renews" to a user who has already cancelled, and offer them a second cancellation
 * the server would reject with a 409.
 *
 * @param {object} plan The overview's plan object.
 * @returns {boolean}
 */
const isCancellationPending = (plan) => plan?.cancellation_requested_at != null;

/**
 * Title-cases a Stripe status for the chip: `active` → "Active", `past_due` → "Past Due".
 *
 * Done in JS rather than with a `capitalize` class so the rendered text is what the DOM actually
 * contains — CSS would leave `past_due` reading "Past_due" and leave the underscore in place for
 * anything reading the text rather than the pixels.
 */
const formatStatus = (status) =>
  String(status || '')
    .split('_')
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

/**
 * The status chip's colour pair (border + text).
 *
 * Figma draws the chip in the success green, not the primary blue the first cut used — but the
 * chip renders whatever Stripe status the subscription is in, so a flat green would paint
 * `past_due` and `canceled` as if they were healthy. Only the healthy statuses take green; the
 * ones the user has to act on take the functional red that the UNSUBSCRIBE button already uses,
 * and anything unrecognised falls back to the neutral grey rather than asserting a judgement.
 */
const statusChipClasses = (status) => {
  switch (String(status || '').toLowerCase()) {
    case 'active':
    case 'trialing':
      return 'border-success-green text-success-green';
    case 'past_due':
    case 'unpaid':
    case 'incomplete':
    case 'incomplete_expired':
    case 'canceled':
      return 'border-functional-red text-functional-red';
    default:
      return 'border-grey2 text-body';
  }
};

/**
 * "$100/month" — the price line Figma puts to the right of the plan name.
 *
 * **ROAR-4945: the contract sends `amount_cents`, in CENTS.** This previously read `plan.amount`,
 * a field the endpoint does not return, so the `== null` guard fired against the shipped backend
 * and the price clause disappeared from the row entirely. Renaming alone would have been the
 * opposite failure — a $100 plan advertised at $10,000 — so the conversion is what makes the
 * rename correct, and the two must never be separated.
 *
 * `amount_cents` NULL-DEGRADES by design: a company with no active price row for its currency gets
 * `{ amount_cents: null, currency: null }` rather than a 500, and the row still renders its name
 * and renewal date without a price.
 *
 * Formatted through `Intl` so a non-USD plan reads "€100/month" rather than an ambiguous bare
 * "100/month". Pinned to `en-US` rather than the system locale so the output does not shift with
 * the CI machine's locale. An unrecognized currency code makes `Intl` throw, so the fallback
 * appends the raw code — still unambiguous, just less pretty.
 */
/**
 * How many minor units make one major unit of a currency.
 *
 * **Not always 100.** Stripe's zero-decimal currencies (JPY, KRW, VND, CLP, …) have no minor
 * unit at all: their `amount_cents` IS the major amount. A flat `/100` would render a
 * ¥10,000/month plan as "¥100/month" — the same class of 100× error the rename was made to avoid,
 * just in the other direction and only for some customers.
 *
 * `Intl` already knows this per currency, so the exponent is read from it rather than kept as a
 * hand-maintained list here that would drift from Stripe's.
 *
 * @param {string} currency ISO code, uppercased.
 * @returns {number} The divisor: 1 for a zero-decimal currency, 100 for most others.
 */
const minorUnitDivisor = (currency) => {
  try {
    const { maximumFractionDigits } = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
    }).resolvedOptions();
    return 10 ** (maximumFractionDigits ?? 2);
  } catch (error) {
    // An unrecognized code: assume the 2-decimal majority rather than 1, since guessing "no minor
    // unit" would overstate the price 100-fold.
    return 100;
  }
};

const formatPrice = (plan) => {
  if (plan?.amount_cents == null) return null;
  const minorAmount = Number(plan.amount_cents);
  // A non-numeric amount is a broken contract, not a free plan — drop the clause rather than
  // rendering "NaN/month".
  if (!Number.isFinite(minorAmount)) return null;

  const interval = plan.interval || 'month';
  const currency = String(plan.currency || '').toUpperCase();
  // With no currency there is no exponent to look up. Assume the 2-decimal majority, matching the
  // unrecognized-code branch above.
  if (!currency) return `${minorAmount / 100}/${interval}`;

  const amount = minorAmount / minorUnitDivisor(currency);
  try {
    const formatted = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
      // Whole amounts stay "$100", not "$100.00", matching the Figma frame — but a fractional one
      // takes BOTH decimals: a flat `minimumFractionDigits: 0` renders 25.5 as "$25.5", which
      // reads as a typo on a price. Fractions became routine with `amount_cents` (2550 → 25.5),
      // where before the value was already in major units and effectively always whole.
      minimumFractionDigits: Number.isInteger(amount) ? 0 : 2,
      maximumFractionDigits: 2,
    }).format(amount);
    return `${formatted}/${interval}`;
  } catch (error) {
    return `${amount} ${currency}/${interval}`;
  }
};

/**
 * Whether a portal url is safe to navigate to.
 *
 * Only absolute `https:` urls pass. The portal destination is external by design (Stripe's own
 * domain), so this cannot be an origin check — but it must not be a bare truthiness check either:
 * assigning a `javascript:` url to `window.location.href` executes it in the current page.
 *
 * @param {string} url Candidate url from the portal-session response.
 * @returns {boolean}
 */
const isSafeRedirectUrl = (url) => {
  if (!url) return false;
  try {
    return new URL(url).protocol === 'https:';
  } catch (error) {
    // Not an absolute url at all.
    return false;
  }
};

const PaymentsInvoices = () => {
  const [popup] = useContext(AlertContext);
  const { data, isLoading, isError } = useBillingOverview();
  const createPortalSession = useCreatePortalSession();
  const cancelSubscription = useCancelSubscription();
  const resumeSubscription = useResumeSubscription();

  // ROAR-5007: `features.billing.hide` removes every billing ACTION (Manage Cards, View Invoices,
  // Unsubscribe/Resume). The read-only plan and card display stays. This sits on top of the role
  // gate below, it does not replace it — and the API still enforces billing permissions itself.
  const { data: loginData } = useValidateLogin();
  const billingActionsHidden = isFeatureHidden(loginData?.user?.features, FEATURE_KEYS.BILLING);

  // Set only by a 409 — see `onUnsubscribe`. Deliberately component state rather than an
  // AlertContext popup: a 409 is a stale view, not a failure (§4 decision 16).
  const [staleNotice, setStaleNotice] = useState(false);

  const plan = data?.plan;
  const paymentMethod = data?.payment_method;
  const pending = isCancellationPending(plan);

  // Once the refetch the 409 triggered comes back showing the pending state, the notice has served
  // its purpose — the disabled UNSUBSCRIBE and "cancels on" line now say the same thing.
  useEffect(() => {
    if (pending) setStaleNotice(false);
  }, [pending]);

  /**
   * Opens the Stripe portal. `flow` is the only difference between the two buttons (AC5, AC6).
   * Redirects with `window.location.href`, matching `api/queries/auth.js:26` — not
   * `window.location.assign`, which the repo's own `window.location` test stubs do not provide.
   */
  const openPortal = async (flow) => {
    try {
      const session = await createPortalSession.mutateAsync({ flow });
      // The destination is whatever the response says, so the scheme is checked before navigating.
      // `window.location.href = 'javascript:…'` EXECUTES in the current page, which would turn any
      // manipulated or malformed portal-session response into script execution. Requiring https
      // costs nothing — a Stripe portal url is always absolute https — and removes the sink.
      // No url at all means there is nowhere to send them; a blank page is worse than staying put.
      if (isSafeRedirectUrl(session?.url)) {
        window.location.href = session.url;
        return;
      }
      if (session?.url) {
        // A url we refuse to follow is a broken contract, not a user error — say so rather than
        // appearing to do nothing.
        await popup({
          variant: 'error',
          title: 'Error',
          description: "We couldn't open the billing portal. Please try again.",
          okayText: 'Okay',
        });
      }
    } catch (error) {
      await popup({
        variant: 'error',
        title: 'Error',
        description: "We couldn't open the billing portal. Please try again.",
        okayText: 'Okay',
      });
    }
  };

  /**
   * `popup` resolves ONLY when the user submits — dismissing leaves the promise pending forever, so
   * the code below simply never runs. That is what makes this read as straight-line confirm-then-act.
   */
  const onUnsubscribe = async () => {
    // The end-of-access date, which is `renews_at` — the same field the plan row reads. It is the
    // only concrete fact in the modal, so it is emphasised; with no date on the plan the clause
    // degrades to the period wording rather than printing "end on null".
    const endsOn = formatDate(plan?.renews_at);

    await popup({
      variant: 'destructive_confirm',
      title: 'Cancel subscription',
      description: (
        <>
          {endsOn ? (
            <>
              Your subscription will end on <span className={'font-bold'}>{endsOn}</span>.
            </>
          ) : (
            <>Your subscription will end at the end of the current billing period.</>
          )}{' '}
          You&apos;ll continue to have full access until then, no further charges will apply. If
          you&apos;d like to continue using the platform after that, you can resubscribe at any
          time.
        </>
      ),
      okayText: 'Unsubscribe',
      cancelText: 'Cancel',
    });

    try {
      setStaleNotice(false);
      await cancelSubscription.mutateAsync();
    } catch (error) {
      if (error?.response?.status === 409) {
        // Someone else cancelled first, or this tab is a refetch behind. The hook has already
        // invalidated BILLING_OVERVIEW; all that is left is to say so inline.
        setStaleNotice(true);
        return;
      }
      await popup({
        variant: 'error',
        title: 'Error',
        description: "We couldn't cancel your subscription. Please try again.",
        okayText: 'Okay',
      });
    }
  };

  const onResume = async () => {
    await popup({
      variant: 'warning',
      title: 'Resume subscription?',
      description: 'Your subscription will continue and renew as normal.',
      okayText: 'Resume',
      cancelText: 'Keep cancellation',
    });

    try {
      await resumeSubscription.mutateAsync();
    } catch (error) {
      await popup({
        variant: 'error',
        title: 'Error',
        description: "We couldn't resume your subscription. Please try again.",
        okayText: 'Okay',
      });
    }
  };

  // Built from the formatted date rather than around it: interpolating a null straight into the
  // template would render the literal "renews null", and `.filter(Boolean)` below cannot drop a
  // string that is non-empty. A plan with neither date simply shows no clause.
  //
  // **ROAR-4945: both branches read `renews_at`.** The pending branch used to read
  // `plan.current_period_end`, which the ratified contract does not return — `renews_at` IS
  // `subscriptions.current_period_end` under the name the pane uses, and while a cancellation pends
  // it is the access-ends date. Against the shipped backend the old read produced `undefined`, so
  // the "cancels on <date>" clause vanished for the entire pending window: exactly the window in
  // which the date is the only thing the user wants to know.
  const renewalDate = formatDate(plan?.renews_at);
  const renewalLine = renewalDate ? `${pending ? 'cancels on' : 'renews'} ${renewalDate}` : null;

  return (
    <div>
      {/* ---- Plan (AC4) ---- */}
      <Typography className={'text-headings text-sm font-semibold'}>Plan</Typography>

      {/* Only when there is nothing to show. A failed REFETCH still holds the previous plan in
          cache, and an error line stacked above a full, readable plan row is worse than either
          state alone. */}
      {isError && !plan && (
        <Typography variant={'body2'} className={'mt-2 text-body'}>
          Plan details are unavailable right now.
        </Typography>
      )}

      {!isLoading && !isError && !plan && (
        <Typography variant={'body2'} className={'mt-2 text-body'}>
          No active subscription.
        </Typography>
      )}

      {plan && (
        <div className={'mt-2 flex items-center justify-between'}>
          <div className={'flex items-center gap-2'}>
            <Typography variant={'body1'} className={'text-headings'}>
              {plan.name}
            </Typography>
            {plan.status && (
              <span
                className={`rounded-full border-[1.5px] px-3 py-0.5 text-xs font-bold ${statusChipClasses(
                  plan.status
                )}`}
              >
                {formatStatus(plan.status)}
              </span>
            )}
          </div>
          <Typography variant={'body2'} className={'text-body'}>
            {[formatPrice(plan), renewalLine].filter(Boolean).join(' · ')}
          </Typography>
        </div>
      )}

      {/* ---- Payment method (AC4, AC6) ---- */}
      <div className={'mt-5 border-t border-[#0000001A] pt-4'}>
        <Typography className={'text-headings text-sm font-semibold'}>Payment Method</Typography>
        <Typography className={'text-body text-xs'}>
          Managed securely through Stripe. Siemba never stores your card details.
        </Typography>

        <div className={'mt-3 flex items-center justify-between'}>
          <div>
            {paymentMethod ? (
              <div className={'flex items-center gap-3'}>
                {/* ROAR-4899: the network mark. `resolvePaymentMethodIcon` always returns a path —
                    the placeholder for a wallet, a bank account or a network we have no art for —
                    so the row keeps one layout whatever the method is. Decorative: the label
                    beside it already names the method, and an alt text would make screen readers
                    say "Visa" twice. Boxed at the tallest asset's intrinsic size (the Discover
                    mark is 38×32, the rest 38×24) with `object-contain`, so every mark sits on the
                    same baseline-centred grid instead of each one setting its own row height. */}
                <img
                  src={resolvePaymentMethodIcon(paymentMethod)}
                  alt={''}
                  aria-hidden={'true'}
                  className={'h-8 w-[38px] shrink-0 object-contain'}
                />
                <div>
                  {/* ROAR-4945: named through the registry, and the masked digits render only when
                      there ARE digits. A wallet or bank-account method can carry a null brand AND a
                      null last4, which the previous unconditional template rendered as the literal
                      "null •••• null". */}
                  {/* ROAR-4945: `uppercase` dropped. It suited a raw brand token ("visa" → "VISA"),
                      but the value is now a designed label from the registry, and the class rendered
                      those as "APPLE PAY" / "SAVED PAYMENT METHOD". The registry uppercases the
                      brand branch itself, so card rows are unchanged. */}
                  <Typography variant={'body1'} className={'text-headings'}>
                    {hasMaskedDigits(paymentMethod)
                      ? `${formatMaskedCardNumber(paymentMethod)}`
                      : resolvePaymentMethodLabel(paymentMethod)}
                  </Typography>
                  {paymentMethod.exp_month && paymentMethod.exp_year && (
                    <Typography variant={'body2'} className={'text-body'}>
                      {`Expires ${String(paymentMethod.exp_month).padStart(2, '0')}/${String(
                        paymentMethod.exp_year
                      ).slice(-2)}`}
                    </Typography>
                  )}
                </div>
              </div>
            ) : (
              // "No card on file" is a CLAIM about the account, so it must not be made while the
              // read is still in flight or after it failed — a user who does have a card would be
              // told they do not. Only an answered, successful read can say this.
              !isLoading &&
              !isError && (
                // A company can legitimately have no card yet; Manage Cards is how one gets added.
                <Typography variant={'body2'} className={'text-body'}>
                  No card on file
                </Typography>
              )
            )}
          </div>

          {!billingActionsHidden && (
            <PermissionProvider roles={[SIEMBA_CUSTOMER]}>
              <Button
                type={'button'}
                size={'small'}
                variant={'outlined'}
                disabled={createPortalSession.isLoading}
                onClick={() => openPortal('payment_method_update')}
              >
                MANAGE CARDS
              </Button>
            </PermissionProvider>
          )}
        </div>
      </div>

      {/* ---- Invoices (AC5, AC10) ---- */}
      <div className={'mt-5 border-t border-[#0000001A] pt-4'}>
        <Typography className={'text-headings text-sm font-semibold'}>Invoices</Typography>
        <Typography className={'text-body text-xs'}>
          Managed securely through Stripe. View and download invoices in the billing portal.
        </Typography>

        {/* AC10: no table, no per-row download — the portal owns invoice resolution entirely. */}
        <div className={'mt-3 flex justify-end'}>
          {!billingActionsHidden && (
            <PermissionProvider roles={[SIEMBA_CUSTOMER]}>
              <Button
                type={'button'}
                size={'small'}
                variant={'outlined'}
                disabled={createPortalSession.isLoading}
                onClick={() => openPortal('default')}
              >
                VIEW INVOICES
              </Button>
            </PermissionProvider>
          )}
        </div>
      </div>

      {/* ---- Cancellation (AC7, §4 decision 16) ---- */}
      <div className={'mt-5 border-t border-[#0000001A] pt-4'}>
        <div className={'flex items-center justify-between'}>
          <div>
            <Typography className={'text-headings text-sm font-semibold'}>
              Cancel Subscription
            </Typography>
            <Typography className={'text-body text-xs'}>
              Access continues through the end of your billing period.
            </Typography>
          </div>

          {!billingActionsHidden && (
            <PermissionProvider roles={[SIEMBA_CUSTOMER]}>
              <div className={'flex items-center gap-2'}>
                {/* Disabled while pending (decision 16) AND while a request is in flight — a
                  double-click would otherwise fire a second cancel that 409s and tell the user
                  their own cancellation was already scheduled. */}
                <Button
                  type={'button'}
                  size={'small'}
                  variant={'outlined'}
                  /* Destructive action, so it carries the red outline rather than the primary one.
                   Applied only while ENABLED: the disabled rule in `Button/buttons.module.scss` is
                   itself `!important`, so an unconditional `!border-functional-red` would win on
                   source order and paint a disabled button red. */
                  className={
                    pending || cancelSubscription.isLoading
                      ? ''
                      : '!border-functional-red !text-functional-red'
                  }
                  disabled={pending || cancelSubscription.isLoading}
                  onClick={onUnsubscribe}
                >
                  UNSUBSCRIBE
                </Button>
                {/* Offered only while a cancellation is pending — it is the way back out. */}
                {pending && (
                  <Button
                    type={'button'}
                    size={'small'}
                    variant={'contained'}
                    disabled={resumeSubscription.isLoading}
                    onClick={onResume}
                  >
                    RESUME
                  </Button>
                )}
              </div>
            </PermissionProvider>
          )}
        </div>

        {staleNotice && (
          <Typography variant={'body2'} className={'mt-2 text-body'}>
            This subscription is already scheduled to cancel.
          </Typography>
        )}
      </div>
    </div>
  );
};

export default PaymentsInvoices;
